import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { Component, effect, ElementRef, inject, Injectable, OnInit, signal, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AttachmentModel, MimeTypesDict } from '@models/module/fields/main';
import { CipoFormModule } from '@shared/modules';
import { ItvDateTimePipe } from '@shared/pipes';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { FileService } from '@shared/services/file.service';
import { AiStore, UserStore } from '@signal-store';
import type { AiReasoningStep } from '@signal-store/models/ai-store.model';
import { AiChatContextSelection, CipoAiService } from '@system/search/cipo-ai/cipo-ai.service';
import { MarkdownModule } from 'ngx-markdown';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

export type AgentRole = 'user' | 'assistant' | 'system';

export enum AgentRunThreadMessageItemType {
  Text = 0,
  Reference = 1,
  FollowUp = 2,
  Download = 3,
}

export enum AgentRunThreadMessageSourceType {
  File = 0,
  Url = 1,
}

export type AiChatFileExportFormat = 'xlsx' | 'csv' | 'pdf' | 'docx' | 'json' | 'txt';

export type AiChatFileExportFormatOption = {
  format: AiChatFileExportFormat;
  labelKey: string;
  fallbackLabel: string;
  icon: string;
};

export type AiChatFileExportData = {
  fileName?: string;
  format?: string;
  title?: string;
  content?: string;
  columns?: string[];
  rows?: string[][];
};

export type AiChatDownloadItemState = {
  downloading: boolean;
  error: boolean;
  lastFormat?: AiChatFileExportFormat;
};

export type AgentRunThread = {
  threadId: string;
  createdAt: string;
  messages: AgentRunThreadMessage[];
};

export type AgentRunThreadMessage = {
  role: AgentRole;
  createdAt: string;
  items: AgentRunThreadMessageItem[];
  reasoningSteps?: AiReasoningStep[];
};

export type AgentRunThreadMessageItem = {
  itemType?: AgentRunThreadMessageItemType;
  text?: string;
  sourceType?: AgentRunThreadMessageSourceType;
  sourceReference?: string;
  fileId?: string;
  fileVersionId?: string;
  fileMimeType?: string;
  followUpPrompt?: string;
  fileExport?: AiChatFileExportData;
};

/**
 * Component-scoped OverlayContainer for the AI sidebar.
 * Keeps MatMenu DOM inside the sidebar, so .main-app-content's overflow:hidden
 * does not clip it. Positioning still uses the real viewport because the
 * _getNarrowedViewportRect patch detects that the host is outside .main-app-content
 * and falls back to CDK's default behavior.
 */
@Injectable()
class SidebarOverlayContainer extends OverlayContainer {
  constructor() {
    super(inject(DOCUMENT));
  }

  attach(sidebarEl: HTMLElement): void {
    if (this._containerElement) return;
    const container = this._document.createElement('div');
    container.classList.add('cdk-overlay-container');
    sidebarEl.appendChild(container);
    this._containerElement = container;
  }
}

@Component({
  selector: 'cipo-ai',
  templateUrl: './cipo-ai.component.html',
  styleUrls: ['./cipo-ai.component.scss'],
  imports: [
    CipoFormModule,
    NgTemplateOutlet,
    NgxSkeletonLoaderModule,
    MarkdownModule,
    ItvDateTimePipe,
    SafeHtmlPipe,
    MatDividerModule,
    MatCheckboxModule,
    MatListModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  providers: [SidebarOverlayContainer, { provide: OverlayContainer, useExisting: SidebarOverlayContainer }],
})
export class CipoAiContainerComponent implements OnInit {
  protected userStore = inject(UserStore);
  protected aiStore = inject(AiStore);
  private cipoAiService = inject(CipoAiService);
  private fileService = inject(FileService);
  private host = inject(ElementRef) as ElementRef<HTMLElement>;
  private sidebarContainer = inject(SidebarOverlayContainer);

  private searchInput = viewChild<ElementRef<HTMLTextAreaElement>>('searchInput');
  private responseContainer = viewChild<ElementRef<HTMLDivElement>>('responseContainer');

  searchControl = new FormControl('');
  restrictToCurrentContextControl = new FormControl(true, { nonNullable: true });
  readonly itemTypes = AgentRunThreadMessageItemType;
  readonly sourceTypes = AgentRunThreadMessageSourceType;
  readonly exportFormatOptions: AiChatFileExportFormatOption[] = [
    {
      format: 'pdf',
      labelKey: 'CIPO_AI.CHAT.DOWNLOAD_FORMAT_PDF',
      fallbackLabel: 'PDF (.pdf)',
      icon: 'picture_as_pdf',
    },
    {
      format: 'docx',
      labelKey: 'CIPO_AI.CHAT.DOWNLOAD_FORMAT_DOCX',
      fallbackLabel: 'Word (.docx)',
      icon: 'description',
    },
    {
      format: 'xlsx',
      labelKey: 'CIPO_AI.CHAT.DOWNLOAD_FORMAT_XLSX',
      fallbackLabel: 'Excel (.xlsx)',
      icon: 'table_view',
    },
    { format: 'csv', labelKey: 'CIPO_AI.CHAT.DOWNLOAD_FORMAT_CSV', fallbackLabel: 'CSV (.csv)', icon: 'csv' },
    { format: 'txt', labelKey: 'CIPO_AI.CHAT.DOWNLOAD_FORMAT_TXT', fallbackLabel: 'Text (.txt)', icon: 'article' },
    {
      format: 'json',
      labelKey: 'CIPO_AI.CHAT.DOWNLOAD_FORMAT_JSON',
      fallbackLabel: 'JSON (.json)',
      icon: 'data_object',
    },
  ];
  readonly downloadStates = signal<Record<string, AiChatDownloadItemState>>({});
  private readonly externalReferenceWindowFeatures = 'noopener,noreferrer';

  ngOnInit(): void {
    this.sidebarContainer.attach(this.host.nativeElement);
  }

  loadingEffect = effect(() => {
    if (this.aiStore.loading()) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  });

  responseEffect = effect(() => {
    const thread = this.aiStore.thread();
    if (thread) {
      this.scrollToBottom();
      // should focus input after the scroll to bottom
      this.focusInput();
    } else {
      this.focusInput();
    }
  });

  search() {
    if (this.aiStore.loading()) return;
    const text = this.searchControl.value;
    if (!text) return;

    this.aiStore.askQuestion({ text, contextSelection: this.getContextSelection() });
    this.searchControl.setValue('');
  }

  isFileReference(item: AgentRunThreadMessageItem): boolean {
    const isReference = item.itemType === this.itemTypes.Reference;
    const isFile = item.sourceType === this.sourceTypes.File;
    return isReference && isFile && !!item.fileId && !!item.fileVersionId && !!item.text;
  }

  isUrlReference(item: AgentRunThreadMessageItem): boolean {
    const isReference = item.itemType === this.itemTypes.Reference;
    const isUrl = item.sourceType === this.sourceTypes.Url;
    return isReference && isUrl && !!item.sourceReference && !!item.text;
  }

  openExternalReference(item: AgentRunThreadMessageItem, event: Event): void {
    event.preventDefault();

    if (!this.isUrlReference(item)) {
      return;
    }

    window.open(item.sourceReference, '_blank', this.externalReferenceWindowFeatures);
  }

  handleAssistantMarkdownClick(event: Event, message: AgentRunThreadMessage): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const anchor = target.closest('a');
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    const linkText = anchor.textContent?.trim() ?? '';
    const rawHref = anchor.getAttribute('href')?.trim() ?? '';
    const resolvedHref = anchor.href?.trim() ?? rawHref;
    const references = this.getReferenceItems(message);

    const fileReference = references.find(
      reference => this.isFileReference(reference) && this.areSameLabel(reference.text, linkText),
    );
    if (fileReference) {
      this.openReferencedFile(fileReference, event);
      return;
    }

    const urlReference = references.find(
      reference =>
        this.isUrlReference(reference) &&
        (this.areSameLabel(reference.text, linkText) ||
          this.areSameUrl(reference.sourceReference, rawHref, resolvedHref)),
    );
    if (urlReference) {
      this.openExternalReference(urlReference, event);
      return;
    }

    if (this.isExternalHttpUrl(resolvedHref)) {
      event.preventDefault();
      window.open(resolvedHref, '_blank', this.externalReferenceWindowFeatures);
    }
  }

  handleAssistantMarkdownKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  }

  isFollowUp(item: AgentRunThreadMessageItem): boolean {
    return item.itemType === this.itemTypes.FollowUp && !!item.followUpPrompt && !!item.text;
  }

  isDownloadItem(item: AgentRunThreadMessageItem): boolean {
    const exportData = item.fileExport;
    const hasData =
      !!exportData?.content?.trim() || (exportData?.columns?.length ?? 0) > 0 || (exportData?.rows?.length ?? 0) > 0;
    return item.itemType === this.itemTypes.Download && !!exportData && hasData;
  }

  getDownloadFileName(item: AgentRunThreadMessageItem, formatOverride?: AiChatFileExportFormat): string {
    const exportData = item.fileExport;
    const targetFormat = formatOverride ?? this.resolveExportFormat(exportData?.format, exportData?.fileName);
    const rawName = (exportData?.fileName || item.text || exportData?.title || 'ai-export').trim();
    const dotIndex = rawName.lastIndexOf('.');
    const rawBaseName = dotIndex > 0 ? rawName.slice(0, dotIndex) : rawName;
    const humanReadableBaseName = rawBaseName.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    return `${humanReadableBaseName || 'ai-export'}.${targetFormat}`;
  }

  getDownloadState(messageIndex: number, itemIndex: number): AiChatDownloadItemState {
    const key = this.getDownloadStateKey(messageIndex, itemIndex);
    return this.downloadStates()[key] ?? { downloading: false, error: false };
  }

  downloadGeneratedFile(
    item: AgentRunThreadMessageItem,
    messageIndex: number,
    itemIndex: number,
    formatOverride?: AiChatFileExportFormat,
    event?: Event,
  ): void {
    event?.preventDefault();

    if (!this.isDownloadItem(item)) {
      return;
    }

    const currentState = this.getDownloadState(messageIndex, itemIndex);
    if (currentState.downloading) {
      return;
    }

    const exportData = item.fileExport;
    const targetFormat =
      formatOverride ?? currentState.lastFormat ?? this.resolveExportFormat(exportData?.format, exportData?.fileName);
    const targetFileName = this.getDownloadFileName(item, targetFormat);
    const key = this.getDownloadStateKey(messageIndex, itemIndex);

    this.downloadStates.update(states => ({
      ...states,
      [key]: { downloading: true, error: false, lastFormat: targetFormat },
    }));

    this.cipoAiService
      .downloadExportFile({
        threadId: this.aiStore.thread()?.threadId || undefined,
        fileName: targetFileName,
        format: targetFormat,
        title: exportData?.title,
        content: exportData?.content,
        columns: exportData?.columns ?? [],
        rows: exportData?.rows ?? [],
      })
      .subscribe({
        next: blob => {
          if (!blob || blob.size === 0) {
            this.downloadStates.update(states => ({
              ...states,
              [key]: { downloading: false, error: true, lastFormat: targetFormat },
            }));
            return;
          }

          this.fileService.downloadFile(blob, targetFileName);
          this.downloadStates.update(states => ({
            ...states,
            [key]: { downloading: false, error: false, lastFormat: targetFormat },
          }));
        },
        error: () => {
          this.downloadStates.update(states => ({
            ...states,
            [key]: { downloading: false, error: true, lastFormat: targetFormat },
          }));
        },
      });
  }

  retryDownloadGeneratedFile(
    item: AgentRunThreadMessageItem,
    messageIndex: number,
    itemIndex: number,
    event?: Event,
  ): void {
    const lastFormat = this.getDownloadState(messageIndex, itemIndex).lastFormat;
    this.downloadGeneratedFile(item, messageIndex, itemIndex, lastFormat, event);
  }

  runFollowUp(item: AgentRunThreadMessageItem, event: MouseEvent): void {
    event.preventDefault();

    const prompt = item.followUpPrompt?.trim();
    if (!prompt || this.aiStore.loading()) {
      return;
    }

    this.searchControl.setValue(prompt);
    this.aiStore.askQuestion({ text: prompt, contextSelection: this.getContextSelection() });
    this.searchControl.setValue('');
  }

  openReferencedFile(item: AgentRunThreadMessageItem, event: Event): void {
    event.preventDefault();

    if (!item.fileId || !item.fileVersionId || !item.text) {
      return;
    }

    const fileMimeType = this.resolveReferencedFileMimeType(item);
    if (!fileMimeType) {
      return;
    }

    const fileId = Number(item.fileId);
    const fileVersionId = Number(item.fileVersionId);
    if (!Number.isInteger(fileId) || !Number.isInteger(fileVersionId)) {
      return;
    }

    const attachment: AttachmentModel = {
      id: fileId,
      name: item.text,
      version: 1,
      versionId: fileVersionId,
      parentId: 0,
      mimeType: fileMimeType,
    };

    this.fileService.openFile([attachment], 0, true);
  }

  shouldRenderStandaloneReference(message: AgentRunThreadMessage, item: AgentRunThreadMessageItem): boolean {
    if (this.isFileReference(item)) {
      return true;
    }

    if (!this.isUrlReference(item)) {
      return false;
    }

    const assistantText = this.getAssistantText(message);
    if (!assistantText) {
      return true;
    }

    const normalizedText = assistantText.toLocaleLowerCase();
    const label = item.text?.trim().toLocaleLowerCase();
    if (label && normalizedText.includes(label)) {
      return false;
    }

    const sourceReference = item.sourceReference?.trim();
    if (sourceReference && normalizedText.includes(sourceReference.toLocaleLowerCase())) {
      return false;
    }

    return true;
  }

  getStandaloneFileReferences(message: AgentRunThreadMessage): AgentRunThreadMessageItem[] {
    return (message.items ?? []).filter(
      item => this.isFileReference(item) && this.shouldRenderStandaloneReference(message, item),
    );
  }

  isFirstStandaloneFileReference(message: AgentRunThreadMessage, item: AgentRunThreadMessageItem): boolean {
    const refs = this.getStandaloneFileReferences(message);
    return refs.length > 0 && refs[0] === item;
  }

  private getReferenceItems(message: AgentRunThreadMessage): AgentRunThreadMessageItem[] {
    return (message.items ?? []).filter(item => item.itemType === this.itemTypes.Reference);
  }

  private resolveReferencedFileMimeType(item: AgentRunThreadMessageItem): AttachmentModel['mimeType'] | undefined {
    const directMimeType = item.fileMimeType?.trim();
    if (directMimeType) {
      return directMimeType as AttachmentModel['mimeType'];
    }

    const extension = this.getFileExtension(item.text);
    if (!extension) {
      return undefined;
    }

    return MimeTypesDict[extension] as AttachmentModel['mimeType'];
  }

  private getFileExtension(fileName: string | undefined): string | undefined {
    if (!fileName) {
      return undefined;
    }

    const extensionIndex = fileName.lastIndexOf('.');
    if (extensionIndex <= 0 || extensionIndex === fileName.length - 1) {
      return undefined;
    }

    return fileName.slice(extensionIndex + 1).toLocaleLowerCase();
  }

  private areSameLabel(left?: string, right?: string): boolean {
    return (left?.trim() ?? '').localeCompare(right?.trim() ?? '', undefined, { sensitivity: 'accent' }) === 0;
  }

  private areSameUrl(referenceUrl: string | undefined, rawHref: string, resolvedHref: string): boolean {
    if (!referenceUrl) {
      return false;
    }

    return (
      this.normalizeUrl(referenceUrl) === this.normalizeUrl(rawHref) ||
      this.normalizeUrl(referenceUrl) === this.normalizeUrl(resolvedHref)
    );
  }

  private normalizeUrl(value: string): string {
    if (!value) {
      return '';
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    try {
      const normalized = new URL(trimmed).toString();
      return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
    } catch {
      return trimmed;
    }
  }

  private isExternalHttpUrl(value: string): boolean {
    return /^https?:\/\//i.test(value?.trim() ?? '');
  }

  private getDownloadStateKey(messageIndex: number, itemIndex: number): string {
    const threadId = this.aiStore.thread()?.threadId ?? 'active';
    return `${threadId}:${messageIndex}:${itemIndex}`;
  }

  private resolveExportFormat(rawFormat?: string, rawFileName?: string): AiChatFileExportFormat {
    const candidate = rawFormat?.trim().replace(/^\./, '').toLocaleLowerCase() ?? '';
    if (this.isSupportedExportFormat(candidate)) {
      return candidate;
    }

    const ext = this.getFileExtension(rawFileName);
    if (ext && this.isSupportedExportFormat(ext)) {
      return ext;
    }

    return 'xlsx';
  }

  private isSupportedExportFormat(value: string): value is AiChatFileExportFormat {
    return this.exportFormatOptions.some(option => option.format === value);
  }

  private getAssistantText(message: AgentRunThreadMessage): string {
    return (message.items ?? [])
      .filter(item => item.itemType === this.itemTypes.Text && !!item.text)
      .map(item => item.text?.trim() ?? '')
      .filter(text => !!text)
      .join('\n');
  }

  private getContextSelection(): AiChatContextSelection {
    const entityInstanceId = this.aiStore.activeEntityInstanceId?.() ?? undefined;
    const projectFolderId = this.aiStore.activeProjectFolderId?.() ?? undefined;

    return {
      restrictToCurrentContext: this.restrictToCurrentContextControl.value,
      programEntityInstanceId: this.userStore.program()?.entityInstanceId,
      projectEntityInstanceId: this.userStore.project()?.entityInstanceId,
      contractEntityInstanceId: this.userStore.contract()?.entityInstanceId,
      ...(entityInstanceId ? { entityInstanceId } : {}),
      ...(projectFolderId ? { projectFolderId } : {}),
    };
  }

  private scrollToBottom() {
    setTimeout(() => {
      const container = this.responseContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  }

  private focusInput() {
    setTimeout(() => {
      const inputElement = this.searchInput()?.nativeElement;
      if (!inputElement) {
        return;
      }

      inputElement.focus();

      const textLength = inputElement.value.length;

      // Set selection range to the end
      inputElement.setSelectionRange(textLength, textLength);
      // Scroll to show the end of the text
      inputElement.scrollLeft = inputElement.scrollWidth;
    }, 200);
  }
}
